// TODO: Add a clear button to the input field.
// TODO: Clear the input field when the user clicks outside of the popover.
// TODO: Infinite scroll for the PopoverContent.
// TODO: Add a loading state for the options.
// TODO: Add a counter for the number of options.

import { AdvancedImage } from '@cloudinary/react'
// import { thumbnail } from '@cloudinary/url-gen/actions/resize'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Button } from '~/components/ui/button.tsx'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from '~/components/ui/command.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '~/components/ui/popover.tsx'
import cld from '~/services/cloudinary/cloudinaryInstance.tsx'

import { cn } from '~/utils/misc.ts'

type Option<T> = T & {
	id: string
	value: string
	label: string
	imgSrc: string
	tags?: string
}

interface Props<T> {
	options: Option<T>[]
	specialOptions: Option<T>[]
	onSelect: (selectedOption: Option<T>) => void
	autoFocus?: boolean
}

function generateThumbnail(imgSrc: string) {
	return cld.image(imgSrc).quality('auto').format('auto')
	// .resize(thumbnail().width(100).height(100).crop('fill'))
}

export function SelectCommand<T = object>(props: Props<T>): React.JSX.Element {
	const { options, specialOptions } = props
	const [open, setOpen] = useState(false)
	const [value, setValue] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [allOptions, setAllOptions] = useState([...specialOptions, ...options])

	useEffect(() => {
		const sortedOptions = [...specialOptions, ...options].sort((a, b) =>
			a.label.toLowerCase().localeCompare(b.label),
		)
		setAllOptions(sortedOptions)
	}, [options, specialOptions])

	// BUG: The tag search fails in certain cases. film-noir tags, and 3d on polygon.
	// seems like hyphen and spaces are issues, possibly other symbols too.

	const filteredOptions = allOptions.filter(option => {
		const searchWords = searchQuery.toLowerCase().match(/[\w-]+/gu) ?? []
		return (
			option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
			searchWords.some(word =>
				option.tags
					?.split(',')
					.some(tag => tag.toLowerCase().startsWith(word.toLowerCase())),
			)
		)
	})

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className="h-16 w-[350px] justify-between bg-white"
				>
					{value ? (
						<>
							{allOptions.find(option => option.value === value) && (
								<AdvancedImage
									cldImg={generateThumbnail(
										allOptions.find(option => option.value === value)
											?.imgSrc as string,
									)}
									alt={`${
										allOptions.find(option => option.value === value)?.label
									} thumbnail`}
									className="mr-2 inline-block h-12 w-12"
								/>
							)}
							<div className="flex flex-col">
								<span>
									{allOptions.find(option => option.value === value)?.label}
								</span>
								<div className="flex flex-row flex-wrap space-x-2">
									{allOptions
										.find(option => option.value === value)
										?.tags?.split(',')
										.map(tag => (
											<span
												key={tag}
												className={cn('text-xs', 'text-gray-500')}
											>
												{tag}
											</span>
										))}
								</div>
							</div>
						</>
					) : (
						'Select option...'
					)}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="max-h-[700px] w-[300px] overflow-y-scroll bg-white p-0">
				<Command>
					<CommandInput
						placeholder="Search option..."
						onValueChange={value => {
							setSearchQuery(value.toLowerCase())
						}}
						autoFocus={props.autoFocus}
					/>
					<CommandEmpty>No options found.</CommandEmpty>
					<CommandGroup>
						{filteredOptions.map(option => (
							<CommandItem
								key={option.id}
								className="hover:bg-gray-200"
								onSelect={() => {
									setValue(option.value === value ? '' : option.value)
									setOpen(false)
									props.onSelect(option)
								}}
							>
								<Check
									className={cn(
										'mr-2 h-4 w-4',
										value === option.value ? 'opacity-100' : 'opacity-0',
									)}
								/>
								<AdvancedImage
									cldImg={generateThumbnail(option.imgSrc)}
									alt={`${option.label} thumbnail`}
									className="mr-2 inline-block h-12 w-12"
								/>
								<div className="flex flex-col">
									<span>{option.label}</span>
									<div className="flex flex-row flex-wrap space-x-2">
										{option.tags?.split(',').map(tag => (
											<span
												key={tag}
												className={cn('text-xs', 'text-gray-500')}
											>
												{tag}
											</span>
										))}
									</div>
								</div>
							</CommandItem>
						))}
					</CommandGroup>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
